import React from 'react';
import { useDrag } from 'react-dnd';
import { TbGripVertical, TbTrash } from 'react-icons/tb';
import { ICollection } from 'types';
import { theme } from 'styles';
import { COLLECTION_TYPE_LABEL, ITEM_TYPE } from './constants';

const { color, radius } = theme;

interface Props {
  order: number;
  collection: ICollection;
  handleRemove: (id: number) => void;
}

function CollectionItem({ order, collection, handleRemove }: Props) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ITEM_TYPE,
    item: { collection },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      data-collection-id={collection.id}
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 64,
        padding: '10px 10px 10px 8px',
        borderRadius: radius.md + 2,
        background: color.white,
        boxShadow: `inset 0 0 0 1px ${color.grey100}`,
        cursor: 'grab',
        userSelect: 'none',
        opacity: isDragging ? 0.4 : 1,
        transition: 'box-shadow 0.15s ease, background-color 0.15s ease',
        ':hover': {
          background: color.grey50,
          boxShadow: `inset 0 0 0 1px ${color.grey200}`,
        },
        ':active': { cursor: 'grabbing' },
      }}
    >
      <TbGripVertical size={18} css={{ flex: 'none', color: color.grey300 }} />
      <span
        css={{
          flex: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: radius.sm,
          background: color.primary,
          color: color.white,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {order}
      </span>
      <div css={{ flex: 1, minWidth: 0 }}>
        <p
          css={{
            fontSize: 15,
            fontWeight: 600,
            color: color.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {collection.name}
        </p>
        {collection.title && (
          <p
            css={{
              marginTop: 3,
              fontSize: 12,
              color: color.textMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {collection.title}
          </p>
        )}
      </div>
      <span
        css={{
          flex: 'none',
          padding: '4px 10px',
          borderRadius: radius.pill,
          background: color.grey75,
          color: color.grey600,
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {COLLECTION_TYPE_LABEL[collection.type] ?? '기본'}
      </span>
      <button
        type="button"
        aria-label={`${collection.name} 삭제`}
        onClick={() => handleRemove(collection.id)}
        css={{
          flex: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: radius.sm + 2,
          color: color.grey400,
          transition: 'background-color 0.15s ease, color 0.15s ease',
          ':hover': { background: color.errorBg, color: color.error },
        }}
      >
        <TbTrash size={18} />
      </button>
    </div>
  );
}

export default CollectionItem;
